import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import moment from 'moment';

import { API_BASE_URL } from '../../config';
// core components
import GridItem from '@/components/Grid/GridItem';
import GridContainer from '@/components/Grid/GridContainer';
import Card from '@/components/Card/Card';
import CardBody from '@/components/Card/CardBody';
import loader from '../../assets/img/loader.svg';
import uploadIcon from '../../assets/img/upload.svg';
import { utcToLocal } from '@/utility';
import { useDataMappingsForUpload } from '@/api/hooks';
import { REFUND_FILE_PROCESS_STATUSES } from '../Dashboard/UploadedFiles';
import useRefundFilesWithSummary from '../../utils/hooks/refund-files/useRefundFilesWithSummary';
import RefundFileCountCell from '../../utils/components/RefundFileCountCell';
import { Typography } from '@mui/material';

const Uploadfile = () => {
  const { t } = useTranslation();
  const [loaded, setLoaded] = useState(false);
  const [, setFacilityFolder] = useState('');
  const [file, setFile] = useState(null);
  const [filesList, setFilesList] = useState([{}]);
  const [uploadFileText, setUploadFileText] = useState('');
  const [uploadFileStatus, setUploadFileStatus] = useState(false);
  const [uploadSuccessMessage, setUploadSuccessMessage] = useState(false);
  const [uploadErrorMessage] = useState(false);
  const [dataMappings, setDataMappings] = useState([]);
  const [selectedDataMapping, setSelectedDataMapping] = useState(null);
  const [user, setUser] = useState(null);
  const {
    dataMappingsForUpload,
    loading: dataMappingForUploadLoading,
  } = useDataMappingsForUpload();

  useEffect(() => {
    if (!uploadFileText) {
      setUploadFileText(t('uploadRefundFile.fileUpload.browse'));
    }
  }, [t, uploadFileText]);

  useEffect(() => {
    if (dataMappingsForUpload.length > 1) {
      setDataMappings(dataMappingsForUpload);
    }
    if (dataMappingsForUpload.length === 1) {
      setSelectedDataMapping(dataMappingsForUpload[0].id);
    }
  }, [dataMappingsForUpload, dataMappingForUploadLoading]);

  const uploadFile = async () => {
    try {
      if (file == null) {
        alert(t('uploadRefundFile.alerts.selectFile'));
        setUploadFileText(t('uploadRefundFile.fileUpload.browse'));
        setUploadFileStatus(false);
        return false;
      } else if (!selectedDataMapping || selectedDataMapping === 'null') {
        alert(t('uploadRefundFile.alerts.selectDataMapping'));
      } else {
        setLoaded(true);
        const formData = new FormData();
        formData.append('file', file);
        formData.append(
          'folder',
          user && user.name.toLowerCase().replace(/ /g, ''),
        );
        formData.append('data_mapping_id', selectedDataMapping);
        formData.append('user_id', user && user.id);

        await axios.post(API_BASE_URL + '/upload_file', formData);

        setFile(null);
        setUploadFileStatus(false);
        setLoaded(false);

        const uploadedFiles = await axios.get(API_BASE_URL + '/getFilesList');

        setFilesList(uploadedFiles && uploadedFiles.data);
        setUploadSuccessMessage(true);
        setUploadFileText(t('uploadRefundFile.fileUpload.browse'));
        setLoaded(false);
      }
    } catch (error) {
      setLoaded(false);
      console.log(error);
    }
  };

  const handleUploadFile = (event) => {
    if (event.target.files[0] !== undefined) {
      setFile(event.target.files[0]);
      setUploadFileText(event.target.files[0].name);
      setUploadFileStatus(true);
    } else {
      alert(t('uploadRefundFile.alerts.selectAFile'));
    }
  };

  useEffect(() => {
    setLoaded(true);
    const state = localStorage['appState'];
    let AppState;
    if (state) {
      AppState = JSON.parse(state);
      setUser(AppState.user);
    }

    setFacilityFolder(
      state.user && state.user.name.toLowerCase().replace(/ /g, ''),
    );
    axios
      .get(API_BASE_URL + '/getFilesList')
      .then((response) => {
        return response;
      })
      .then((uploadedFiles) => {
        setFilesList(uploadedFiles && uploadedFiles.data);
        setLoaded(false);
      })
      .catch((err) => {
        console.error(err);
        setLoaded(false);
      });
  }, []);

  const refundFilesWithSummary = useRefundFilesWithSummary(filesList);

  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <h2 className='subHeading'>{t('uploadRefundFile.title')}</h2>
        <Card className='dashboardCard table_card_height'>
          <CardBody>
            <div>
              {loaded && (
                <div className='loaders'>
                  <img src={loader} />
                </div>
              )}
              {uploadSuccessMessage && (
                <div className='uploadTxt'>
                  <p>{t('uploadRefundFile.messages.uploadSuccess')}</p>{' '}
                </div>
              )}
              {uploadErrorMessage && (
                <div className='uploadTxt'>
                  <p style={{ color: 'red' }}>
                    {t('uploadRefundFile.messages.fileAlreadyUploaded')}
                  </p>{' '}
                </div>
              )}
              <div className='t-header uploadCard'>
                <div className='t-header-left'>
                  <input
                    type='file'
                    onChange={handleUploadFile}
                    value=''
                    id='logoimg'
                  />
                  <img src={uploadIcon} />
                  <p>{uploadFileText}</p>
                </div>
              </div>
              <div className='uploadTxt'>
                {uploadFileStatus && (
                  <button className='btn' onClick={uploadFile}>
                    {t('uploadRefundFile.fileUpload.uploadButton')}
                  </button>
                )}
                <p>
                  <a href={API_BASE_URL + '/getSampleRefundFile'}>
                    {' '}
                    {t('uploadRefundFile.fileUpload.downloadTemplate')}{' '}
                  </a>{' '}
                </p>
              </div>
            </div>
            {!!dataMappings.length && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                }}>
                <div className='custom-search-box'>
                  <div className='custom-select'>
                    <select
                      value={selectedDataMapping}
                      className='full-width-elem'
                      id='status'
                      onChange={(e) => setSelectedDataMapping(e.target.value)}>
                      <option style={{ fontWeight: 'bold' }} value='null'>
                        {t('uploadRefundFile.dropdown.dataMappings')}
                      </option>
                      {dataMappings.map((item) => {
                        return (
                          <option
                            value={item.id}
                            className='parent'
                            style={{ fontWeight: 'bold' }}
                            key={item.id}>
                            {item.name}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <span className='search-icon'></span>
                </div>
              </div>
            )}
          </CardBody>
        </Card>

        <Card className='dashboardCard table_card_height'>
          <CardBody>
            <table width='100%' id='template' className='paymentTable'>
              <thead>
                <tr>
                  <th width='10%'>
                    {t('uploadRefundFile.table.uploadedDate')}
                  </th>
                  <th width='25%'>{t('uploadRefundFile.table.fileName')}</th>
                  <th width='15%'>
                    {t('uploadRefundFile.table.facilityName')}
                  </th>
                  <th width='15%'>{t('uploadRefundFile.table.fileStatus')}</th>
                  <th width='15%'>{t('uploadRefundFile.table.totalAmount')}</th>
                  <th width='15%'>{t('uploadRefundFile.table.recordCount')}</th>
                  <th width='15%'>
                    {t('uploadRefundFile.table.dataMappingName')}
                  </th>
                  <th width='15%'>
                    {t('uploadRefundFile.table.processedDate')}
                  </th>
                </tr>
              </thead>
              {refundFilesWithSummary && refundFilesWithSummary.length > 0 ? (
                <tbody>
                  {refundFilesWithSummary.map((obj, index) => (
                    <tr className='txtalign' key={index}>
                      <td width='10%'>
                        <Typography fontSize={'14px'}>
                          {utcToLocal(obj.created_at, 'MM/DD/YYYY')}
                        </Typography>
                      </td>
                      <td width='25%'>
                        <Typography fontSize={'14px'}>
                          {obj && obj.filename}
                        </Typography>
                      </td>
                      <td width='15%'>
                        <Typography fontSize={'14px'}>
                          {obj && obj.name ? obj.name : '-'}
                        </Typography>
                      </td>
                      <td width='15%'>
                        <Typography fontSize={'14px'}>
                          {REFUND_FILE_PROCESS_STATUSES[obj.process_status]}
                        </Typography>
                      </td>
                      <td width='15%'>
                        <Typography fontSize={'14px'}>
                          {obj.total_amount == 0
                            ? t('uploadRefundFile.table.emptyFile')
                            : obj.total_amount}
                        </Typography>
                      </td>
                      <td width='15%'>
                        <RefundFileCountCell refund={obj} />
                      </td>
                      <td>
                        <Typography fontSize={'14px'}>
                          {obj.data_mapping_group_name
                            ? obj.data_mapping_group_name
                            : '-'}
                        </Typography>
                      </td>
                      <td width='15%'>
                        <Typography fontSize={'14px'}>
                          {obj && obj.process_status == 0
                            ? t('uploadRefundFile.table.notApplicable')
                            : obj && obj.upload_date}{' '}
                          {obj.processed_at
                            ? moment(obj.processed_at).format(
                                'MM-DD-YYYY HH:mm',
                              )
                            : '-'}
                        </Typography>
                      </td>
                    </tr>
                  ))}
                </tbody>
              ) : (
                <tbody>
                  <tr>
                    <td>{t('uploadRefundFile.table.noFileUploaded')}</td>
                  </tr>
                </tbody>
              )}
            </table>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
};

export default Uploadfile;
